import axios from 'axios';
import { API_BASE_URL } from '../config';
import { authHeaders, asErr } from '../utils/apiHelpers';

const pick = (obj, ...keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== '') return v;
  }
  return undefined;
};

const toInt = (v) => {
  if (v === undefined || v === null) return undefined;
  const s = String(v).trim();
  if (!s) return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
};

// ==============================
// Address overlay (READ)
// GET /api/employee/address/overlay
// ==============================
export async function fetchAddressOverlay() {
  try {
    const res = await axios.get(`${API_BASE_URL}/api/employee/address/overlay`, {
      headers: authHeaders(),
    });
    return res.data;
  } catch (err) {
    throw asErr('Failed to fetch address overlay', err);
  }
}

// ==============================
// Address draft (WRITE)
// POST /api/employee/address/draft
// NOTE: server expects snake_case due to [JsonPropertyName(...)].
// ==============================
export async function saveAddressDraft(model: any = {}) {
  // 🔁 Map UI snake/camel → wire snake_case (what your DTO attributes specify)
  const body = {
    short_address:     pick(model, 'address_line1', 'short_address', 'shortAddress', 'address1'),
    street:            pick(model, 'address_line2', 'street', 'address2'),
    district_name:     pick(model, 'district', 'district_name'),
    city_id:           toInt(pick(model, 'city_id', 'cityId')),
    region_id:         toInt(pick(model, 'region_id', 'regionId')),
    postal_code:       pick(model, 'postal_code', 'postalCode', 'zip'),
    building_number:   pick(model, 'building_no', 'buildingNumber'),
    extension_number:  pick(model, 'additional_no', 'extension_number', 'additionalNo'),
    unit_no:           pick(model, 'unit_no', 'unitNo'),
    latitude:          pick(model, 'latitude'),
    longitude:         pick(model, 'longitude'),

    // NEW: proof of national address upload id (GUID as string)
    proof_of_national_address_upload_id: pick(
      model,
      'proof_of_national_address_upload_id', // our preferred key
      'proofUploadId',                       // accept these aliases from UI if used
      'proofUploadID'
    ),
  };

  // prune undefined
  Object.keys(body).forEach((k) => body[k] === undefined && delete body[k]);

  // 🔎 TEMP DEBUG — remove after confirming
  alert(`[DEBUG] Address draft payload:\n${JSON.stringify(body, null, 2)}`);

  const primary = `${API_BASE_URL}/api/employee/address/draft`;
  const fallback = `${API_BASE_URL}/api/employee/tabs/address/draft`;

  try {
    await axios.post(primary, body, {
      headers: authHeaders({ 'Content-Type': 'application/json' }),
    });
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      // support old route if primary isn’t registered yet
      await axios
        .post(fallback, body, {
          headers: authHeaders({ 'Content-Type': 'application/json' }),
        })
        .catch((e2) => {
          throw asErr('Failed to save address draft', e2);
        });
    } else {
      throw asErr('Failed to save address draft', err);
    }
  }

  // Re-fetch overlay so UI shows latest
  return await fetchAddressOverlay();
}

// ==============================
// Uploads (CREATE + helper URLs)
// POST /api/uploads  (multipart/form-data)
// ==============================
export async function uploadTemp(file) {
  if (!file) throw new Error('No file selected');

  const form = new FormData();
  form.append('file', file);

  try {
    // NOTE: do NOT set 'Content-Type' manually; the browser sets boundary.
    const res = await axios.post(`${API_BASE_URL}/api/uploads`, form, {
      headers: authHeaders(),
    });
    // { uploadId, fileName, contentType, sizeBytes, url }
    return res.data;
  } catch (err) {
    throw asErr('Failed to upload file', err);
  }
}

// === Uploads (auth fetch -> Blob/ObjectURL) ==========================
export async function fetchUploadBlob(uploadId) {
  try {
    const res = await axios.get(`${API_BASE_URL}/api/uploads/${uploadId}`, {
      headers: authHeaders(),
      responseType: 'blob',
    });
    return res.data; // Blob
  } catch (err) {
    throw asErr('Failed to download file', err);
  }
}

export async function fetchThumbBlob(uploadId) {
  try {
    const res = await axios.get(`${API_BASE_URL}/api/uploads/${uploadId}/thumb`, {
      headers: authHeaders(),
      responseType: 'blob',
    });
    return res.data; // Blob (JPEG)
  } catch (err) {
    throw asErr('Failed to fetch thumbnail', err);
  }
}

// Turn a Blob into an object URL (remember to revoke later)
export function blobToObjectUrl(blob) {
  return URL.createObjectURL(blob);
}

// Open a file in a new tab or trigger download, still using auth
export async function openUploadInNewTab(uploadId, fileName = 'file') {
  const blob = await fetchUploadBlob(uploadId);
  const url = blobToObjectUrl(blob);

  // Try to open a new tab first (works for images/PDF in most browsers)
  const win = window.open(url, '_blank', 'noopener');
  if (!win) {
    // fallback: trigger a download
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  // cleanup a bit later
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

